'use client'

import { Toaster } from 'react-hot-toast'
import {
  DehydratedState,
  HydrationBoundary,
  QueryClientProvider
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { SessionProvider } from 'next-auth/react'
import NextNProgress from 'nextjs-progressbar'

import { CustomerAdaptersProvider } from 'composition/adapters/CustomerAdaptersProvider'
import { ProductAdaptersProvider } from 'composition/adapters/ProductAdaptersProvider'
import MemoryCleanupProvider from 'composition/memoryCleanup/MemoryCleanupProvider'
import StoreInitializer from 'composition/storeInitializer'
import { IdentifyUserInExternals } from 'composition/tracking/IdentifyUserInExternals'
import { DeliverySelectionProvider, SyneriseCartUpdate } from 'modules/cart'
import { RibbonProvider } from 'modules/content'
import {
  DeliverySupportContextProvider,
  OnboardingContextProvider
} from 'modules/delivery'
import { EditSaleOrderModalsProvider } from 'modules/saleOrders'
import { getQueryClient } from 'shared/api'
import { OverlayStackProvider } from 'shared/contexts/OverlayStackContext'
import ClientOnly from 'shared/ui/ClientOnly/ClientOnly'
import { OfflineProvider } from 'shared/ui/Offline/OfflineContext'
import { ZIndex } from 'shared/ui/types'
import { theme } from 'shared/ui/utils/designTokens'
import { isDev } from 'shared/utils/env'
import { addTestId } from 'shared/utils/tests'

const Providers = ({
  children,
  dehydratedState
}: {
  children: React.ReactNode
  dehydratedState: DehydratedState
}) => {
  return (
    <SessionProvider refetchWhenOffline={false}>
      <QueryClientProvider client={getQueryClient()}>
        <HydrationBoundary state={dehydratedState}>
          <StoreInitializer />
          <MemoryCleanupProvider />
          <OverlayStackProvider>
            <DeliverySupportContextProvider>
              <DeliverySelectionProvider>
                <CustomerAdaptersProvider>
                  <ProductAdaptersProvider>
                    <OnboardingContextProvider>
                      <RibbonProvider>
                        <EditSaleOrderModalsProvider>
                          <NextNProgress
                            showOnShallow
                            height={3}
                            color={theme.colors.brand.secondary}
                            options={{ showSpinner: false }}
                            aria-label='Ładowanie...'
                          />
                          <Toaster
                            toastOptions={{
                              duration: 5000,
                              style: {
                                zIndex: ZIndex.TOASTER
                              }
                            }}
                            {...addTestId('toast-message')}
                          />
                          <ClientOnly>
                            {/* <BrowserExtensionHandler /> */}
                            <IdentifyUserInExternals />
                            <SyneriseCartUpdate />
                          </ClientOnly>
                          <OfflineProvider>
                            {children}
                            <ClientOnly>
                              {isDev && (
                                <ReactQueryDevtools initialIsOpen={false} />
                              )}
                            </ClientOnly>
                          </OfflineProvider>
                        </EditSaleOrderModalsProvider>
                      </RibbonProvider>
                    </OnboardingContextProvider>
                  </ProductAdaptersProvider>
                </CustomerAdaptersProvider>
              </DeliverySelectionProvider>
            </DeliverySupportContextProvider>
          </OverlayStackProvider>
        </HydrationBoundary>
      </QueryClientProvider>
    </SessionProvider>
  )
}

export default Providers
